import { ENTRIES_KIND, OPERATOR_LABELS, fieldHoldsChoiceList, operatorLabelFor, operatorValuesForKind } from './kqlBuilder';

/**
 * THE CONDITION BUILDER'S COMPARISONS (KPR-949).
 *
 * The filter builder and the condition builder (the editor behind rules,
 * thresholds, reminders, generators and tag rules) ask the same questions of
 * the same fields, so they read ONE table: `OPERATORS_BY_KIND` in
 * `kqlBuilder.js`, and its words (`OPERATOR_LABELS`, `operatorLabelFor`). A
 * comparison added there — "is any of" was the first — reaches both.
 *
 * The condition builder differs from the filter builder in a few places, on
 * purpose, and this file is the ONE place that says where and why. A test
 * (`__tests__/conditionOperators.test.js`) holds the two to it: for every kind,
 * the condition builder's set is the filter builder's, less what is omitted
 * here, plus what is added here — and nothing else may differ.
 */

/** Condition-builder kind -> the filter-builder kind whose table it reads. */
export const FILTER_KIND_OF_CONDITION_KIND = {
	text: 'text',
	choice: 'choice',
	number: 'number',
	date: 'date',
	measurement: 'measurement',
	currency: 'currency',
	boolean: 'boolean',
	lookup: 'lookup',
	file: 'file',
	entries: ENTRIES_KIND,
	tag: 'tag',
	user: 'user',
	datePart: 'datePart',
};

/**
 * Where the condition builder deliberately differs from the filter builder.
 *
 * `omit`: filter-builder comparisons it does NOT offer. `add`: comparisons it
 * offers that the filter builder does not. Every entry says why.
 */
export const CONDITION_OPERATOR_DIFFERENCES = {
	// A rule stored `status contains "prog"` long before the filter builder
	// dropped it for a choice; those rules must keep opening as rows.
	choice: {
		add: ['contains', 'ncontains'],
		reason: 'Rules stored with contains on a choice keep opening as rows.',
	},
	// The filter builder asks "is empty" of a number; a rule also asks "has one".
	number: {
		add: ['nempty'],
		reason: 'A rule can ask that a number has been filled in.',
	},
	date: {
		omit: ['gte', 'lte'],
		reason: 'A date reads on / before / after in a rule; the inclusive forms are not offered (a stored one opens as text).',
	},
	boolean: {
		add: ['nempty'],
		reason: 'A rule can ask that a yes/no has been answered.',
	},
	// A rule's lookup value is a picked item. The filter builder's lookup is
	// also matched by title (contains); it offers is any of (KPR-1712) but not
	// is not, is none of or has one.
	lookup: {
		omit: ['contains'],
		add: ['ne', 'nempty', 'nin'],
		reason: 'A rule picks the item, so title matching is not offered; the API compiles is not, has one and is none of on a lookup.',
	},
	// A stored query's emptiness on tags is not answered until the API's
	// KEEPR_TAGS_STORED_EMPTINESS gate opens (T3 review fix I1).
	tag: {
		omit: ['empty', 'nempty'],
		reason: 'The API cannot yet answer emptiness on tags in a stored condition.',
	},
};

// The order a set is listed in: membership beside equality, then ranges, then
// text matching, then emptiness. A kind whose words read better in another
// order (a date: on, before, after) says so in `ORDER_OF_KIND`.
const ORDER = ['eq', 'ne', 'in', 'nin', 'gt', 'gte', 'lt', 'lte', 'contains', 'ncontains', 'has', 'nhas', 'empty', 'nempty'];
const ORDER_OF_KIND = {
	date: ['eq', 'lt', 'gt', 'empty', 'nempty'],
	[ENTRIES_KIND]: ['has', 'nhas', 'nempty', 'empty'],
};

// Words the condition builder has always worn where the filter builder's would
// read differently: the symbols of a number, and a date's on / before / after.
const MATH_LABELS = { eq: '=', ne: '≠', gte: '≥', lte: '≤' };
const LABELS_OF_KIND = {
	number: MATH_LABELS,
	measurement: MATH_LABELS,
	currency: MATH_LABELS,
	date: { eq: 'on', lt: 'before', gt: 'after' },
};
// A Choice holding a list: the comparisons the filter builder has no list
// reading for (`LIST_OPERATOR_LABELS`).
const LIST_CHOICE_LABELS = { contains: 'has one containing', ncontains: 'has none containing' };

/** The comparison values the condition builder offers a kind, in order. */
export function conditionOperatorValues(kind) {
	const filterKind = FILTER_KIND_OF_CONDITION_KIND[kind] || 'text';
	const difference = CONDITION_OPERATOR_DIFFERENCES[kind] || {};
	const omit = new Set(difference.omit || []);
	const values = [...operatorValuesForKind(filterKind).filter(value => !omit.has(value)), ...(difference.add || [])]
		.filter((value, index, all) => all.indexOf(value) === index);
	const order = ORDER_OF_KIND[kind] || ORDER;
	return values.sort((a, b) => order.indexOf(a) - order.indexOf(b));
}

/** Operator options (antd Select shape) for a field of this condition-builder kind. */
export function conditionOperatorOptions(kind, field) {
	const holdsList = fieldHoldsChoiceList(field);
	return conditionOperatorValues(kind).map(value => ({
		value,
		label: (holdsList && LIST_CHOICE_LABELS[value])
			|| LABELS_OF_KIND[kind]?.[value]
			|| operatorLabelFor(field, value)
			|| OPERATOR_LABELS[value]
			|| value,
	}));
}
